import React from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import "./CarpetaAnio.css";

/**
 * Componente digital para carpetas por año (estilo sobrio, claro y elegante).
 * Por defecto muestra fondo claro casi blanco con acentos guinda.
 * En hover pasa a guinda institucional.
 * La carpeta del año actual conserva el fondo guinda sólido como punto focal único.
 * 
 * @param {string|number} anio - Año de los convenios (ej. 2026).
 * @param {string} url - URL o ruta interna de destino (ej. "/Convenios2026").
 * @param {string} [to] - Alias para `url` compatible con React Router.
 * @param {string} [ariaLabel] - Etiqueta accesible (por defecto: "Convenios {anio}").
 * @param {boolean} [isCurrent=false] - Indica si es el año actual para destacarlo.
 * @param {number} [delayMs=0] - Retardo para la animación de entrada escalonada.
 * @param {string} [className] - Clases CSS adicionales.
 */
const CarpetaAnio = ({
  anio,
  url,
  to,
  ariaLabel,
  isCurrent = false,
  delayMs = 0,
  className = "",
}) => {
  const targetUrl = to || url || "#";
  const label = ariaLabel || `Convenios ${anio}`;
  const isExternal =
    typeof targetUrl === "string" &&
    (targetUrl.startsWith("http://") || targetUrl.startsWith("https://"));

  const animationStyle = delayMs > 0 ? { animationDelay: `${delayMs}ms` } : undefined;
  const currentClass = isCurrent ? "is-current" : "";
  const linkClasses = `carpeta-moderna-link ${currentClass} ${className}`.trim();

  const folderContent = (
    <>
      {/* Insignia sobria para destacar el año actual */}
      {isCurrent && (
        <span className="folder-tag-actual">
          Actual
        </span>
      )}

      {/* Capa trasera: pestaña izquierda superior */}
      <div className="folder-back" aria-hidden="true">
        <div className="folder-tab" />
      </div>

      {/* Capa intermedia: hoja de papel que se asoma y sube en hover */}
      <div className="folder-paper" aria-hidden="true">
        <div className="folder-paper-line line-1" />
        <div className="folder-paper-line line-2" />
        <div className="folder-paper-line line-3" />
      </div>

      {/* Capa frontal: cuerpo de la carpeta */}
      <div className="folder-front">
        {/* Icono de documento sutil en color acento */}
        <svg
          className="folder-front-icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
        </svg>

        {/* Píldora con el año */}
        <span className="folder-year-badge">{anio}</span>
      </div>
    </>
  );

  if (isExternal) {
    return (
      <a
        href={targetUrl}
        className={linkClasses}
        aria-label={label}
        style={animationStyle}
        target="_blank"
        rel="noopener noreferrer"
      >
        {folderContent}
      </a>
    );
  }

  return (
    <Link
      to={targetUrl}
      className={linkClasses}
      aria-label={label}
      style={animationStyle}
    >
      {folderContent}
    </Link>
  );
};

CarpetaAnio.propTypes = {
  anio: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  url: PropTypes.string,
  to: PropTypes.string,
  ariaLabel: PropTypes.string,
  isCurrent: PropTypes.bool,
  delayMs: PropTypes.number,
  className: PropTypes.string,
};

export default CarpetaAnio;
